// src/storage/adaptiveEngine.ts — Adaptive Learning & Scaffolding Engine (FR-ADAPT-001..003)
// Implements difficulty tier classification (Easy/Medium/Hard/Challenge),
// recency-weighted topic mastery scoring (EMA, alpha=0.3),
// difficulty escalation (2 consecutive correct) / de-escalation (2 consecutive incorrect),
// and distractor-elimination scaffolding for ADHD focus.

export type DifficultyTier = 'easy' | 'medium' | 'hard' | 'challenge';

export const TIER_ORDER: Record<DifficultyTier, number> = {
  easy: 1,
  medium: 2,
  hard: 3,
  challenge: 4,
};

export interface TierMeta {
  label: string;
  badge: string;
  color: string;
  bg: string;
}

export const TIER_METAS: Record<DifficultyTier, TierMeta> = {
  easy: {
    label: 'Easy',
    badge: '🌱 Shallows',
    color: '#3D5A45',
    bg: 'rgba(61, 90, 69, 0.15)',
  },
  medium: {
    label: 'Medium',
    badge: '🌊 Reef Crest',
    color: '#1A365D',
    bg: 'rgba(26, 54, 93, 0.12)',
  },
  hard: {
    label: 'Hard',
    badge: '⚓ Deep Trench',
    color: '#E5A93C',
    bg: 'rgba(229, 169, 60, 0.2)',
  },
  challenge: {
    label: 'Challenge',
    badge: '⚡ Abyssal Spire',
    color: '#FF6B4A',
    bg: 'rgba(255, 107, 74, 0.15)',
  },
};

export interface TopicMasteryRecord {
  topic: string;
  masteryScore: number; // 0.0 to 1.0 (recency-weighted EMA with alpha = 0.3)
  totalAttempts: number;
  correctAttempts: number;
  consecutiveCorrect: number;
  consecutiveIncorrect: number;
  currentTier: DifficultyTier;
  lastUpdated: number;
}

export interface AttemptResult {
  record: TopicMasteryRecord;
  tierTransition: 'escalated' | 'deescalated' | 'maintained';
  isScaffoldRecommended: boolean;
}

const STORAGE_KEY = 'clawey_adaptive_mastery_v1';
export const EMA_ALPHA = 0.3; // Recency weighting constant (FR-ADAPT-003)

/** Calculate exponential moving average for mastery */
export function calculateEma(
  previousEma: number | null,
  isCorrect: boolean,
  alpha: number = EMA_ALPHA
): number {
  const currentVal = isCorrect ? 1.0 : 0.0;
  if (previousEma === null) {
    return currentVal;
  }
  const updated = alpha * currentVal + (1 - alpha) * previousEma;
  return Math.round(updated * 1000) / 1000;
}

/** Get next tier upwards */
export function getNextTierUp(current: DifficultyTier): DifficultyTier {
  switch (current) {
    case 'easy':
      return 'medium';
    case 'medium':
      return 'hard';
    case 'hard':
    case 'challenge':
      return 'challenge';
  }
}

/** Get next tier downwards */
export function getNextTierDown(current: DifficultyTier): DifficultyTier {
  switch (current) {
    case 'challenge':
      return 'hard';
    case 'hard':
      return 'medium';
    case 'medium':
    case 'easy':
      return 'easy';
  }
}

export class AdaptiveEngine {
  /** Load all topic mastery records from localStorage */
  static loadMastery(): Record<string, TopicMasteryRecord> {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        return JSON.parse(raw) as Record<string, TopicMasteryRecord>;
      }
    } catch {
      // Ignored
    }
    return {};
  }

  /** Save all topic mastery records to localStorage */
  static saveMastery(records: Record<string, TopicMasteryRecord>): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(records));
    } catch {
      // Ignored
    }
  }

  /** Get or initialize a topic mastery record */
  static getTopicRecord(topic: string, defaultTier: DifficultyTier = 'easy'): TopicMasteryRecord {
    const records = this.loadMastery();
    if (records[topic]) {
      return records[topic]!;
    }
    return {
      topic,
      masteryScore: 0.5,
      totalAttempts: 0,
      correctAttempts: 0,
      consecutiveCorrect: 0,
      consecutiveIncorrect: 0,
      currentTier: defaultTier,
      lastUpdated: Date.now(),
    };
  }

  /**
   * Record Tudor's answer attempt for a topic (FR-ADAPT-001, FR-ADAPT-002, FR-ADAPT-003).
   * - Computes recency-weighted accuracy via Exponential Moving Average (alpha = 0.3)
   * - 2 consecutive correct -> tier up
   * - 2 consecutive incorrect -> tier down + scaffold flag
   */
  static recordAttempt(topic: string, isCorrect: boolean): AttemptResult {
    const records = this.loadMastery();
    const existing = records[topic];

    const currentRecord: TopicMasteryRecord = existing ?? {
      topic,
      masteryScore: isCorrect ? 1.0 : 0.0,
      totalAttempts: 0,
      correctAttempts: 0,
      consecutiveCorrect: 0,
      consecutiveIncorrect: 0,
      currentTier: 'easy',
      lastUpdated: Date.now(),
    };

    // 1. Update EMA mastery
    const previousEma = existing ? existing.masteryScore : null;
    const newMastery = calculateEma(previousEma, isCorrect, EMA_ALPHA);

    currentRecord.masteryScore = newMastery;
    currentRecord.totalAttempts += 1;
    if (isCorrect) {
      currentRecord.correctAttempts += 1;
      currentRecord.consecutiveCorrect += 1;
      currentRecord.consecutiveIncorrect = 0;
    } else {
      currentRecord.consecutiveIncorrect += 1;
      currentRecord.consecutiveCorrect = 0;
    }
    currentRecord.lastUpdated = Date.now();

    // 2. Evaluate Tier Escalation / De-escalation
    let tierTransition: 'escalated' | 'deescalated' | 'maintained' = 'maintained';
    let isScaffoldRecommended = false;

    if (currentRecord.consecutiveCorrect >= 2) {
      const nextUp = getNextTierUp(currentRecord.currentTier);
      if (nextUp !== currentRecord.currentTier) {
        currentRecord.currentTier = nextUp;
        tierTransition = 'escalated';
        currentRecord.consecutiveCorrect = 0;
      }
    } else if (currentRecord.consecutiveIncorrect >= 2) {
      const nextDown = getNextTierDown(currentRecord.currentTier);
      if (nextDown !== currentRecord.currentTier) {
        currentRecord.currentTier = nextDown;
        tierTransition = 'deescalated';
      }
      isScaffoldRecommended = true;
      currentRecord.consecutiveIncorrect = 0;
    }

    records[topic] = currentRecord;
    this.saveMastery(records);

    return {
      record: currentRecord,
      tierTransition,
      isScaffoldRecommended,
    };
  }

  /** Get recommended tier for a given topic */
  static getRecommendedTier(topic: string): DifficultyTier {
    const rec = this.getTopicRecord(topic);
    return rec.currentTier;
  }

  /** Get mastery percentage for display (0-100) */
  static getMasteryPercentage(topic: string): number {
    const rec = this.getTopicRecord(topic);
    return Math.round(rec.masteryScore * 100);
  }

  /**
   * Scaffold options: When Tudor struggles, eliminate 1 or 2 incorrect options
   * while always guaranteeing the correct answer is preserved (FR-ADAPT-002).
   */
  static scaffoldOptions(options: string[], correctAnswer: string): string[] {
    if (options.length <= 2) return [...options];

    const wrongOptions = options.filter((opt) => opt !== correctAnswer);
    // Keep 1 incorrect distractor alongside the correct answer (2 options total)
    const preservedWrong = wrongOptions[0] ?? wrongOptions[wrongOptions.length - 1];

    // Reconstruct list in original relative order
    return options.filter((opt) => opt === correctAnswer || opt === preservedWrong);
  }

  /** Determine question difficulty tier from explicit tag or deterministic fallback */
  static getQuestionDifficulty(q: { difficulty?: DifficultyTier; id?: string; topic?: string }): DifficultyTier {
    if (q.difficulty && TIER_ORDER[q.difficulty]) {
      return q.difficulty;
    }
    const idNum = parseInt((q.id ?? '').replace(/\D/g, ''), 10) || 1;
    const tierCycle: DifficultyTier[] = ['easy', 'medium', 'hard', 'challenge'];
    return tierCycle[(idNum - 1) % tierCycle.length] ?? 'medium';
  }

  /** Generate friendly Socratic scaffold hint */
  static getScaffoldHint(topic: string): string {
    return `Clawey focused your choices for "${topic}". Two unlikely options were cleared away to help you spot the true pattern!`;
  }

  /** Reset mastery for a topic */
  static resetTopic(topic: string): void {
    const records = this.loadMastery();
    delete records[topic];
    this.saveMastery(records);
  }

  /** Reset all adaptive learning data */
  static resetAll(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Ignored
    }
  }
}
